/**
 * openAIP airspace as filterable 3D volumes.
 *
 * The raster layer this replaces was one flat picture: it could not be filtered and it could not
 * carry a height. The MVT tiles carry `type`, `icao_class` and both vertical limits as real
 * attributes, which is what makes both possible.
 *
 * Attribute shapes, read off an actual tile over the Swabian Alb rather than assumed:
 *
 * ```
 * type: 'other' | 'tma' | 'ctr' | 'tmz' | 'rmz' | 'fir' | 'fis_sector' | 'restricted' | …
 * icao_class: 'a'..'g' | 'unclassified'
 * lower_limit_value: 2500     lower_limit_unit: 'FT'   lower_limit_reference_datum: 'GND'
 * upper_limit_value: 100      upper_limit_unit: 'FL'   upper_limit_reference_datum: 'STD'
 * name_label: 'E 2500 FT GND - FL100'
 * ```
 *
 * Two things that matter and are easy to get wrong:
 *
 * **Plain controlled airspace has no distinctive `type`.** Class C, D and E arrive as
 * `type: 'other'` with `icao_class` carrying the letter. Filtering for "Klasse E" is therefore a
 * two-clause filter, exactly as openAIP's own style does it. Filtering on `type` alone silently
 * shows nothing.
 *
 * **Upstream maxzoom is 12.** The source has to say so, or MapLibre asks for z13 and up, gets
 * nothing, and the airspace disappears the moment you zoom in — which looks like a bug in the
 * filter.
 */

import type { ExpressionSpecification, FilterSpecification } from 'maplibre-gl';

export const AIRSPACE_SOURCE = 'airspace';
export const AIRSPACE_SOURCE_LAYER = 'airspaces';
/** openAIP does not generate tiles above this, and overzooming a vector source loses geometry. */
export const AIRSPACE_MAX_ZOOM = 12;

export interface AirspaceGroup {
  readonly id: string;
  readonly label: string;
  /** Shown by default: what a glider pilot actually has to avoid. */
  readonly defaultOn: boolean;
  readonly colour: string;
  readonly filter: FilterSpecification;
}

/**
 * The groups worth offering, and deliberately not one entry per `type`.
 *
 * There are forty-odd type strings and a pilot thinks in about six categories. FIR and the FIS
 * sectors are excluded from the defaults entirely: they cover the whole country from the ground
 * up, so leaving them on paints the entire map one colour and hides everything underneath.
 */
export const AIRSPACE_GROUPS: readonly AirspaceGroup[] = [
  {
    id: 'ctr',
    label: 'CTR',
    defaultOn: true,
    colour: '#f25c3c',
    filter: ['match', ['get', 'type'], ['ctr'], true, false],
  },
  {
    id: 'tma',
    label: 'TMA / CTA',
    defaultOn: true,
    colour: '#e08a3c',
    filter: ['match', ['get', 'type'], ['tma', 'cta'], true, false],
  },
  {
    id: 'tmz',
    label: 'TMZ / RMZ',
    defaultOn: true,
    colour: '#c46be0',
    filter: ['match', ['get', 'type'], ['tmz', 'rmz', 'tiz', 'tia'], true, false],
  },
  {
    id: 'restricted',
    label: 'ED-R / ED-D',
    defaultOn: true,
    colour: '#e0405c',
    filter: ['match', ['get', 'type'], ['restricted', 'danger', 'prohibited'], true, false],
  },
  {
    id: 'cd',
    label: 'Klasse C/D',
    defaultOn: true,
    colour: '#4f8ef0',
    // Two clauses, because plain controlled airspace is type 'other' plus the class letter.
    filter: [
      'all',
      ['match', ['get', 'type'], ['other'], true, false],
      ['match', ['get', 'icao_class'], ['c', 'd'], true, false],
    ],
  },
  {
    id: 'e',
    label: 'Klasse E',
    defaultOn: false,
    colour: '#2eb2c4',
    filter: [
      'all',
      ['match', ['get', 'type'], ['other'], true, false],
      ['match', ['get', 'icao_class'], ['e'], true, false],
    ],
  },
  {
    id: 'gliding',
    label: 'Segelflugsektoren',
    defaultOn: true,
    colour: '#7ad678',
    filter: [
      'match',
      ['get', 'type'],
      ['gliding_sector', 'vfr_sector', 'aerial_sporting_recreational'],
      true,
      false,
    ],
  },
  {
    id: 'fir',
    label: 'FIR / FIS',
    defaultOn: false,
    colour: '#8892a4',
    filter: ['match', ['get', 'type'], ['fir', 'uir', 'fis_sector', 'acc_sector'], true, false],
  },
];

const FT_TO_M = 0.3048;
/** A flight level is hundreds of feet. */
const FL_TO_M = 100 * FT_TO_M;

/**
 * A vertical limit, in metres **above the terrain surface** — which is what MapLibre's
 * `fill-extrusion-base` and `-height` mean once terrain is enabled.
 *
 * `GND` converts exactly, because AGL is already what the property means. `MSL` and `STD` (a
 * flight level) do not: turning those into a height above ground needs the terrain elevation at
 * every point, and there is no expression for that. They are therefore drawn relative to a
 * single reference elevation — the home field's — so a flight level is right over the field and
 * off by the local relief elsewhere. Over the Swabian Alb that is a couple of hundred metres.
 *
 * That approximation is why every polygon keeps its `name_label` and why the popup quotes it
 * verbatim: the drawing is for orientation, the text is the authority.
 */
export function limitToMetres(prefix: 'lower' | 'upper', refElevationM: number): ExpressionSpecification {
  const value = ['to-number', ['get', `${prefix}_limit_value`], 0] as ExpressionSpecification;
  const unit = ['get', `${prefix}_limit_unit`] as ExpressionSpecification;
  const datum = ['get', `${prefix}_limit_reference_datum`] as ExpressionSpecification;

  // Metres above mean sea level first, then reduced to a height above ground.
  const msl: ExpressionSpecification = [
    'case',
    ['==', unit, 'FL'],
    ['*', value, FL_TO_M],
    ['==', unit, 'M'],
    value,
    ['*', value, FT_TO_M],
  ];

  return [
    'max',
    0,
    [
      'case',
      ['==', datum, 'GND'],
      // Already a height above the ground. `M` and `FT` both occur with a GND datum.
      ['case', ['==', unit, 'M'], value, ['*', value, FT_TO_M]],
      ['-', msl, refElevationM],
    ],
  ];
}

/** The colour expression: one `case` chain over the group filters, so a feature picks its own. */
export function airspaceColour(groups: readonly AirspaceGroup[]): ExpressionSpecification {
  const chain: unknown[] = ['case'];
  for (const g of groups) {
    chain.push(g.filter, g.colour);
  }
  chain.push('#8892a4');
  return chain as unknown as ExpressionSpecification;
}

/** `any` of the enabled groups, or a filter that matches nothing when none are. */
export function enabledFilter(enabled: ReadonlySet<string>): FilterSpecification {
  const active = AIRSPACE_GROUPS.filter((g) => enabled.has(g.id));
  if (active.length === 0) return ['==', ['get', 'type'], ' __none__'] as FilterSpecification;
  return ['any', ...active.map((g) => g.filter)] as FilterSpecification;
}

export const DEFAULT_AIRSPACE_GROUPS: ReadonlySet<string> = new Set(
  AIRSPACE_GROUPS.filter((g) => g.defaultOn).map((g) => g.id),
);

/** The `airports` source-layer travels in the same tile as the airspace. */
export const AIRPORT_SOURCE_LAYER = 'airports';

/**
 * Airfields worth drawing, split by how far out they still deserve the space.
 *
 * ## The type names here were wrong, and silently
 *
 * They were written from openAIP's documented enum names — `international`, `airport`,
 * `af_mil_civil`, `landing_strip`, `agricultural`, `altiport`, `ul`, `water`. **The vector
 * tiles use different strings**, and querying a real tile is the only way to find that out:
 *
 * ```
 * ad_closed  ad_mil  af_civil  af_water  apt  apt_mil_civil  gliding
 * heli_civil heli_mil intl_apt light_aircraft ls  ls_agri  ls_alti
 * ```
 *
 * Only four of the eleven names matched. Everything else fell through the `match` and was never
 * drawn — which meant **the map showed no major airports at all**: Stuttgart, Munich and
 * Frankfurt are `apt` and `intl_apt`, and neither string existed in the list. Nothing errored;
 * the features simply were not there, and a missing airport looks exactly like an area with no
 * airports.
 *
 * ## The split
 *
 * Zoomed out to a country, four thousand airfields is a grey mat with no information in it. The
 * commercial ones stay — they are the landmarks anyone orients by — and everything else appears
 * from `MINOR_AIRPORT_MIN_ZOOM`, where there is room for it.
 *
 * Excluded outright: closed aerodromes and heliports. A glider cannot land on a hospital
 * helipad, openAIP returns dozens around any town, and a closed field is worse than useless
 * because it looks like an option.
 */
export const MAJOR_AIRPORT_TYPES = ['intl_apt', 'apt', 'apt_mil_civil'] as const;

export const MINOR_AIRPORT_TYPES = [
  'gliding',
  'af_civil',
  'af_water',
  'light_aircraft',
  'ad_mil',
  'ls',
  'ls_agri',
  'ls_alti',
] as const;

/** Below this only the commercial airports are drawn. */
export const MINOR_AIRPORT_MIN_ZOOM = 8.5;

export const LANDABLE_AIRPORT_TYPES = [
  ...MAJOR_AIRPORT_TYPES,
  ...MINOR_AIRPORT_TYPES,
] as const;

const typeFilter = (types: readonly string[]): FilterSpecification => [
  'match',
  ['get', 'type'],
  [...types],
  true,
  false,
];

export const AIRPORT_FILTER: FilterSpecification = typeFilter(LANDABLE_AIRPORT_TYPES);
export const MAJOR_AIRPORT_FILTER: FilterSpecification = typeFilter(MAJOR_AIRPORT_TYPES);
export const MINOR_AIRPORT_FILTER: FilterSpecification = typeFilter(MINOR_AIRPORT_TYPES);
